import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import { Section } from 'tio-reimagined/components/layout';
import { PhChatCircle, PhArrowRight } from 'ember-phosphor-icons';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type CommentModel from 'tio-data/models/comment';

const MAX_PREVIEW_LENGTH = 200;

interface RecentCommentPreviewSignature {
  Args: {
    model: TasApplicationModel;
    /** Called when the user clicks "Go to Comments"; receives the tab the comment belongs to */
    onViewMore: (tab: 'approver' | 'employee') => void;
  };
}

export default class RecentCommentPreview extends Component<RecentCommentPreviewSignature> {
  /**
   * Approver-to-approver comments:
   * Has the 'approver' tag but NOT 'Partner Messaging' or 'employee'
   */
  get approverComments(): CommentModel[] {
    return [...(this.args.model.comments ?? [])]
      .filter(
        (comment) =>
          !comment.isNew &&
          comment.tags?.some((tag) => tag === 'approver') &&
          !comment.tags?.includes('Partner Messaging') &&
          !comment.tags?.includes('employee')
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  /**
   * Approver-to-employee comments:
   * Has the 'Partner Messaging' or 'employee' tag
   */
  get employeeComments(): CommentModel[] {
    return [...(this.args.model.comments ?? [])]
      .filter(
        (comment) =>
          !comment.isNew &&
          comment.tags?.some((tag) => tag === 'Partner Messaging' || tag === 'employee')
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  get totalComments(): number {
    return this.approverComments.length + this.employeeComments.length;
  }

  get mostRecentComment(): CommentModel | null {
    const all = [...this.approverComments, ...this.employeeComments].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return all[0] ?? null;
  }

  /**
   * Which drawer tab houses the most recent comment
   */
  get commentSource(): 'approver' | 'employee' {
    if (!this.mostRecentComment) return 'approver';
    return this.employeeComments.includes(this.mostRecentComment) ? 'employee' : 'approver';
  }

  get authorName(): string {
    const comment = this.mostRecentComment;
    if (!comment) return '';
    const person = comment.person;
    if (person?.firstName && person?.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    return person?.fullName || comment.commentor || '';
  }

  get formattedDate(): string {
    const comment = this.mostRecentComment;
    if (!comment?.createdAt) return '';
    return new Date(comment.createdAt).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /**
   * Plain-text body of the most recent comment.
   * TAS approval responses store JSON `{ title, body }`; we extract .body for those.
   */
  get commentBody(): string {
    const comment = this.mostRecentComment;
    if (!comment) return '';
    if (comment.isTasApproval) {
      try {
        return comment.tasApprovalNote.body;
      } catch {
        return comment.comment ?? '';
      }
    }
    return comment.comment ?? '';
  }

  get isTruncated(): boolean {
    return this.commentBody.length > MAX_PREVIEW_LENGTH;
  }

  get truncatedBody(): string {
    if (this.isTruncated) {
      return this.commentBody.slice(0, MAX_PREVIEW_LENGTH) + '...';
    }
    return this.commentBody;
  }

  <template>
    {{#if this.mostRecentComment}}
      <Section>
        <:body>
          <div
            class="max-w-3xl p-4 border border-gray-200 rounded-lg bg-white"
            data-test-recent-comment-preview
          >
            <div class="flex items-center gap-2 mb-3">
              <PhChatCircle class="text-gray-500 w-5 h-5" />
              <h3 class="text-base font-semibold text-gray-800">
                {{t "tuition_assistance.application_approval.comments"}}
                <span class="text-gray-500 font-normal">({{this.totalComments}})</span>
              </h3>
            </div>

            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
              {{t "tuition_assistance.application_approval.most_recent"}}
            </p>

            <div class="flex items-center gap-2 mb-1">
              <span class="text-sm font-semibold text-gray-800">{{this.authorName}}</span>
              <span class="text-xs text-gray-500">{{this.formattedDate}}</span>
            </div>

            <div class="flex items-end justify-between gap-4">
              <p class="text-sm text-gray-700 leading-relaxed">
                {{this.truncatedBody}}
              </p>

              <button
                type="button"
                class="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors focus:outline-none"
                {{on "click" (fn @onViewMore this.commentSource)}}
                data-test-go-to-comments
              >
                {{t "tuition_assistance.application_approval.go_to_comments"}}
                <PhArrowRight class="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </:body>
      </Section>
    {{/if}}
  </template>
}
